import * as THREE from 'three';

// Built once, shared across all racers — keeps the scene cheap when we have
// a full field of 4 humanoids + bots.
const SHARED = (() => {
  const headGeo = new THREE.SphereGeometry(0.42, 14, 12);
  const torsoGeo = new THREE.CylinderGeometry(0.42, 0.32, 1.1, 14);
  const armGeo = new THREE.CapsuleGeometry(0.14, 0.55, 4, 8);
  const thighGeo = new THREE.CylinderGeometry(0.18, 0.16, 0.44, 8);
  const shinGeo = new THREE.CylinderGeometry(0.15, 0.16, 0.46, 8);
  const handGeo = new THREE.SphereGeometry(0.18, 8, 8);
  const hatGeo = new THREE.ConeGeometry(0.48, 0.5, 14);
  const hatBrimGeo = new THREE.TorusGeometry(0.42, 0.08, 8, 14);
  const pomGeo = new THREE.SphereGeometry(0.14, 8, 8);
  const boardGeo = new THREE.BoxGeometry(0.42, 0.08, 1.7);
  const boardNoseGeo = new THREE.CylinderGeometry(0.21, 0.21, 0.08, 12, 1, false, 0, Math.PI);
  return {
    headGeo, torsoGeo, armGeo, thighGeo, shinGeo, handGeo,
    hatGeo, hatBrimGeo, pomGeo, boardGeo, boardNoseGeo,
    skinMat: new THREE.MeshStandardMaterial({ color: 0xf5d6b1, roughness: 0.7 }),
    pantsMat: new THREE.MeshStandardMaterial({ color: 0x1f2937, roughness: 0.85 }),
    glovesMat: new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.85 }),
    boardTopMat: new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.5 }),
  };
})();

// Hip Y when standing — picked so the foot (end of straight shin) lands at
// y=0.025, matching the previous capsule-leg model's foot height.
const HIP_Y_STAND = 0.925;
// Hip Y when crouching, computed for the bend angles below so the foot
// stays planted on the board.
const HIP_Y_CROUCH = 0.713;
// Leg-bend angles at hip and knee while crouching.
const HIP_BEND = 0.7;     // thigh tilts forward
const KNEE_BEND = -1.4;   // shin folds back (relative to thigh)
// Torso pitch forward at the waist while crouching.
const TORSO_BEND = 0.5;

// Cached canvas textures for the place-label sprites (1st/2nd/3rd/4th).
// Built lazily on first use so the Press Start 2P web font has had time to
// load by the time a race actually ends.
const PLACE_TEXTURES = {};
const PLACE_LABELS = { 1: '1ST', 2: '2ND', 3: '3RD', 4: '4TH' };
const PLACE_COLORS = { 1: '#fde047', 2: '#cbd5e1', 3: '#b45309', 4: '#94a3b8' };

function placeTexture(place) {
  if (PLACE_TEXTURES[place]) return PLACE_TEXTURES[place];
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.font = 'bold 78px "Press Start 2P", system-ui, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 10;
  ctx.strokeStyle = '#0f172a';
  ctx.fillStyle = PLACE_COLORS[place] || '#ffffff';
  const text = PLACE_LABELS[place] || '?';
  ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  PLACE_TEXTURES[place] = tex;
  return tex;
}

// Racer is one rider on the slope: humanoid body + snowboard. The board
// sits directly under the top-level group; everything from the hips up
// lives in a bodyAnchor sub-group that drops + bends when crouching, so
// the snowboard stays on the snow while the rider's knees bend visibly.
export class Racer {
  constructor(scene, palette) {
    if (!palette) {
      throw new Error('Racer: palette is required (see RACER_PALETTES)');
    }
    const jacketMat = new THREE.MeshStandardMaterial({ color: palette.jacket, roughness: 0.85 });
    const hatMat = new THREE.MeshStandardMaterial({ color: palette.hat, roughness: 0.85 });
    const pomMat = new THREE.MeshStandardMaterial({ color: palette.pom, roughness: 0.85 });

    this.group = new THREE.Group();

    // --- Snowboard (stays at the racer's foot level always) ---
    const board = new THREE.Mesh(SHARED.boardGeo, SHARED.boardTopMat);
    board.position.y = 0.04;
    board.castShadow = true;
    board.receiveShadow = true;
    this.group.add(board);
    for (const sign of [-1, 1]) {
      const cap = new THREE.Mesh(SHARED.boardNoseGeo, SHARED.boardTopMat);
      cap.position.set(0, 0.04, sign * 0.85);
      cap.rotation.x = Math.PI / 2;
      cap.rotation.z = sign > 0 ? 0 : Math.PI;
      this.group.add(cap);
    }

    // --- Body anchor: represents the hip joint. Position.y drops on crouch
    // so the bent legs still reach the board and the upper body drops too. ---
    this.bodyAnchor = new THREE.Group();
    this.bodyAnchor.position.y = HIP_Y_STAND;
    this.group.add(this.bodyAnchor);

    // --- Legs: per side, hip → thigh → knee → shin ---
    this.legs = [];
    for (const side of [-1, 1]) {
      const hipGroup = new THREE.Group();
      hipGroup.position.set(side * 0.18, 0, 0);
      this.bodyAnchor.add(hipGroup);

      // Thigh hangs from the hip down to the knee.
      const thigh = new THREE.Mesh(SHARED.thighGeo, SHARED.pantsMat);
      thigh.position.y = -0.22;
      thigh.castShadow = true;
      hipGroup.add(thigh);

      // Knee joint sits at the bottom of the thigh.
      const kneeGroup = new THREE.Group();
      kneeGroup.position.y = -0.44;
      hipGroup.add(kneeGroup);

      // Shin hangs from the knee down to the foot.
      const shin = new THREE.Mesh(SHARED.shinGeo, SHARED.pantsMat);
      shin.position.y = -0.23;
      shin.castShadow = true;
      kneeGroup.add(shin);

      this.legs.push({ hipGroup, kneeGroup });
    }

    // --- Upper body: torso/arms/head/hat. All Y values are RELATIVE to the
    // hip (subtract HIP_Y_STAND from the previous world-Y values). ---
    this.upperBody = new THREE.Group();
    this.bodyAnchor.add(this.upperBody);

    const torso = new THREE.Mesh(SHARED.torsoGeo, jacketMat);
    torso.position.y = 0.325; // was world 1.25
    torso.castShadow = true;
    this.upperBody.add(torso);

    for (const side of [-1, 1]) {
      const arm = new THREE.Mesh(SHARED.armGeo, jacketMat);
      arm.position.set(side * 0.55, 0.325, 0.05);
      arm.rotation.z = side * 0.25;
      arm.castShadow = true;
      this.upperBody.add(arm);

      const hand = new THREE.Mesh(SHARED.handGeo, SHARED.glovesMat);
      hand.position.set(side * 0.7, -0.075, 0.1);
      this.upperBody.add(hand);
    }

    const head = new THREE.Mesh(SHARED.headGeo, SHARED.skinMat);
    head.position.y = 1.125;
    head.castShadow = true;
    this.upperBody.add(head);

    const hat = new THREE.Mesh(SHARED.hatGeo, hatMat);
    hat.position.y = 1.625;
    hat.castShadow = true;
    this.upperBody.add(hat);

    const brim = new THREE.Mesh(SHARED.hatBrimGeo, hatMat);
    brim.position.y = 1.395;
    brim.rotation.x = Math.PI / 2;
    this.upperBody.add(brim);

    const pom = new THREE.Mesh(SHARED.pomGeo, pomMat);
    pom.position.y = 1.925;
    this.upperBody.add(pom);

    // Walk every mesh under this group and clone its material. That way
    // setStunned() can change opacity on just THIS racer without affecting
    // the shared materials all other racers use.
    this._materials = [];
    this.group.traverse((obj) => {
      if (obj.isMesh && obj.material) {
        obj.material = obj.material.clone();
        this._materials.push(obj.material);
      }
    });

    scene.add(this.group);
  }

  // While stunned, drop opacity so the player visibly reads as "in a
  // timeout". opacity = 1.0 when not stunned restores normal rendering.
  setStunned(stunned) {
    const opacity = stunned ? 0.45 : 1.0;
    for (const m of this._materials) {
      m.transparent = stunned;
      m.opacity = opacity;
    }
  }

  // While shielded, wrap the racer in a translucent blue sphere so other
  // players can see they're invulnerable. Lazily creates the bubble mesh
  // on first activation since most racers don't pick up a shield.
  setShielded(shielded) {
    if (shielded && !this._shield) {
      const geo = new THREE.SphereGeometry(1.2, 18, 14);
      const mat = new THREE.MeshStandardMaterial({
        color: 0x60a5fa,
        emissive: 0x1d4ed8,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.32,
        roughness: 0.2,
        metalness: 0.1,
        depthWrite: false,
      });
      this._shield = new THREE.Mesh(geo, mat);
      this._shield.position.y = 1.0;
      this.group.add(this._shield);
    }
    if (this._shield) this._shield.visible = shielded;
  }

  // buried = true plants the racer face-first into the snow: the group is
  // raised by ~1m so the head winds up below the surface and the board
  // (the now-top of the rotated rig) pokes out. The terrain mesh occludes
  // anything below it, giving the "only board visible" look.
  setPose(position, yaw, flipAngle = 0, leanAngle = 0, tucking = false, buried = false) {
    const yOffset = buried ? 1.0 : 0;
    this.group.position.set(position.x, position.y + yOffset, position.z);
    // YXZ order: yaw first (world heading), then X (front flip around the
    // racer's lateral axis), then Z (lean around the forward axis).
    this.group.rotation.order = 'YXZ';
    this.group.rotation.y = yaw;
    this.group.rotation.x = flipAngle;
    this.group.rotation.z = leanAngle;

    this._applyTuck(!!tucking);
  }

  _applyTuck(tucking) {
    if (tucking) {
      this.bodyAnchor.position.y = HIP_Y_CROUCH;
      this.upperBody.rotation.x = TORSO_BEND;
      for (const leg of this.legs) {
        leg.hipGroup.rotation.x = HIP_BEND;
        leg.kneeGroup.rotation.x = KNEE_BEND;
      }
    } else {
      this.bodyAnchor.position.y = HIP_Y_STAND;
      this.upperBody.rotation.x = 0;
      for (const leg of this.legs) {
        leg.hipGroup.rotation.x = 0;
        leg.kneeGroup.rotation.x = 0;
      }
    }
  }

  // Show a floating "1ST/2ND/3RD/4TH" sprite above this racer's head.
  // Reused across calls — if the place is unchanged, no work is done.
  setPlaceLabel(place) {
    if (!place || place < 1 || place > 4) {
      this.hidePlaceLabel();
      return;
    }
    if (this._labelSprite && this._labelPlace === place) return;
    if (!this._labelSprite) {
      this._labelSprite = new THREE.Sprite();
      this._labelSprite.scale.set(2.6, 1.3, 1);
      // Float above the head — head world Y ≈ group.position.y + 2.05 when
      // standing, so a local Y of 3.4 puts the label ~1.3m above the head.
      this._labelSprite.position.set(0, 3.4, 0);
      this.group.add(this._labelSprite);
    }
    this._labelSprite.material = new THREE.SpriteMaterial({
      map: placeTexture(place),
      transparent: true,
    });
    this._labelPlace = place;
  }

  hidePlaceLabel() {
    if (!this._labelSprite) return;
    this.group.remove(this._labelSprite);
    this._labelSprite.material?.dispose();
    this._labelSprite = null;
    this._labelPlace = null;
  }

  destroy() {
    this.hidePlaceLabel();
    this.group.parent?.remove(this.group);
    // Geometries and materials are shared; only the group needs disposal.
  }
}
